import { useEffect, useState } from 'react';
import type { AccesoDeInicio, Navegacion } from '../../api/operaciones';
import { Icono } from '../../componentes/Icono';
import { dibujoDe } from '../../marco/iconosMenu';
import { CASILLAS, gruposDelCajon, moverCasilla, ponerEnCasilla } from './accesos';

type Eleccion = { tipo: 'casilla'; indice: number } | { tipo: 'acceso'; clave: string } | null;

/**
 * «Tus accesos» en modo «Cambiar accesos» (G129; componentes.md §12d): el botón pasa a «Listo», las casillas se marcan y
 * debajo se abre el cajón con los accesos («Sugeridos para ti» y por categoría; los que ya están, con ✓ y sin acción).
 * Cambiar uno por otro: casilla → acceso o acceso → casilla. Mover: casilla → casilla. Esc deshace la elección y, sin
 * elección, sale. Cada cambio se guarda (`onGuardar`) y se anuncia.
 */
export function CambiarAccesos({
    navegacion,
    elegidos,
    hayGuardados,
    onGuardar,
    onRestablecer,
    onListo,
}: {
    navegacion: Pick<Navegacion, 'shortcuts' | 'items'>;
    elegidos: readonly AccesoDeInicio[];
    /** Si hay algo guardado («Restablecer» apagado si ya están los de por defecto). */
    hayGuardados: boolean;
    onGuardar: (claves: string[]) => Promise<void>;
    onRestablecer: () => Promise<void>;
    onListo: () => void;
}) {
    const [eleccion, setEleccion] = useState<Eleccion>(null);
    const [vivo, setVivo] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const claves = elegidos.map((a) => a.key);
    const grupos = gruposDelCajon(navegacion, claves);
    const nombre = (clave: string) => navegacion.shortcuts.find((a) => a.key === clave)?.label ?? clave;

    useEffect(() => {
        const tecla = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') {
                return;
            }
            if (eleccion !== null) {
                setEleccion(null);
            } else {
                onListo();
            }
        };
        window.addEventListener('keydown', tecla);
        return () => {
            window.removeEventListener('keydown', tecla);
        };
    }, [eleccion, onListo]);

    const guardar = (nuevas: string[], anuncio: string) => {
        setEleccion(null);
        setOcupado(true);
        onGuardar(nuevas)
            .then(() => {
                setVivo(anuncio);
            })
            .catch(() => {
                setVivo('No se ha podido guardar el cambio. Prueba otra vez.');
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    const tocarCasilla = (indice: number) => {
        if (eleccion?.tipo === 'acceso') {
            guardar(ponerEnCasilla(claves, indice, eleccion.clave), `${nombre(eleccion.clave)} está ahora en la casilla ${String(indice + 1)}.`);
        } else if (eleccion?.tipo === 'casilla' && eleccion.indice !== indice) {
            guardar(moverCasilla(claves, eleccion.indice, indice), `Casillas ${String(eleccion.indice + 1)} y ${String(indice + 1)} cambiadas de sitio.`);
        } else {
            setEleccion(eleccion?.tipo === 'casilla' ? null : { tipo: 'casilla', indice });
        }
    };
    const tocarAcceso = (clave: string) => {
        if (eleccion?.tipo === 'casilla') {
            guardar(ponerEnCasilla(claves, eleccion.indice, clave), `${nombre(clave)} está ahora en la casilla ${String(eleccion.indice + 1)}.`);
        } else {
            setEleccion(eleccion?.tipo === 'acceso' && eleccion.clave === clave ? null : { tipo: 'acceso', clave });
        }
    };
    const casillas = Array.from({ length: Math.min(CASILLAS, Math.max(elegidos.length, 1) + (elegidos.length < CASILLAS ? 1 : 0)) }, (_, i) => elegidos[i] ?? null);
    const ayuda =
        eleccion === null
            ? 'Toca una casilla y luego el acceso que quieres en ella, o dos casillas para cambiarlas de sitio.'
            : eleccion.tipo === 'casilla'
              ? 'Ahora toca el acceso que quieres en esa casilla, u otra casilla para cambiarlas de sitio.'
              : 'Ahora toca la casilla donde lo quieres.';

    return (
        <section aria-labelledby="rv-tus-accesos" className="rv-accesos rv-accesos--editando">
            <div className="rv-accesos__cab">
                <h2 id="rv-tus-accesos">Tus accesos</h2>
                <button type="button" className="rv-accesos__listo" onClick={onListo}>
                    <Icono nombre="check" />
                    <span>Listo</span>
                </button>
            </div>
            <div className="rv-accesos__rejilla">
                {casillas.map((a, i) => (
                    <button
                        key={a?.key ?? `vacia-${String(i)}`}
                        type="button"
                        className={`rv-accesos__baldosa rv-accesos__casilla${eleccion?.tipo === 'casilla' && eleccion.indice === i ? ' rv-accesos__casilla--elegida' : ''}${eleccion?.tipo === 'acceso' ? ' rv-accesos__casilla--espera' : ''}`}
                        aria-pressed={eleccion?.tipo === 'casilla' && eleccion.indice === i}
                        aria-label={a === null ? `Casilla ${String(i + 1)}, vacía` : `Casilla ${String(i + 1)}: ${a.label}`}
                        disabled={ocupado}
                        onClick={() => {
                            tocarCasilla(i);
                        }}
                    >
                        {a !== null && (
                            <span className="rv-accesos__icono" style={{ background: a.primary ? 'var(--rv-red)' : 'var(--rv-ink)' }}>
                                <Icono nombre={dibujoDe(a.icon)} />
                            </span>
                        )}
                        <span className="rv-accesos__texto">{a?.label ?? 'Vacía'}</span>
                    </button>
                ))}
            </div>
            <div className="rv-accesos__cajon">
                <div className="rv-accesos__cajon-cab">
                    <div>
                        <strong>Cambia tus accesos</strong>
                        <p>{ayuda}</p>
                    </div>
                    <button
                        type="button"
                        className="rv-accesos__restablecer"
                        disabled={!hayGuardados || ocupado}
                        onClick={() => {
                            setEleccion(null);
                            setOcupado(true);
                            onRestablecer()
                                .then(() => {
                                    setVivo('Tus accesos vuelven a ser los de siempre.');
                                })
                                .catch(() => {
                                    setVivo('No se ha podido restablecer. Prueba otra vez.');
                                })
                                .finally(() => {
                                    setOcupado(false);
                                });
                        }}
                    >
                        Restablecer
                    </button>
                </div>
                {grupos.map((g) => (
                    <div key={g.clave} className="rv-accesos__grupo" role="group" aria-label={g.titulo}>
                        <span className={`rv-accesos__ceja${g.clave === 'sugeridos' ? ' rv-accesos__ceja--roja' : ''}`}>{g.titulo}</span>
                        <div className="rv-accesos__pildoras">
                            {g.accesos.map((a) => {
                                const ya = claves.includes(a.key);
                                return (
                                    <button
                                        key={a.key}
                                        type="button"
                                        className={`rv-accesos__pildora${ya ? ' rv-accesos__pildora--ya' : ''}${eleccion?.tipo === 'acceso' && eleccion.clave === a.key ? ' rv-accesos__pildora--elegida' : ''}`}
                                        disabled={ya || ocupado}
                                        aria-pressed={eleccion?.tipo === 'acceso' && eleccion.clave === a.key}
                                        onClick={() => {
                                            tocarAcceso(a.key);
                                        }}
                                    >
                                        <Icono nombre={ya ? 'check' : dibujoDe(a.icon)} />
                                        <span>{a.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <span className="rv-sr" aria-live="polite">
                {vivo}
            </span>
        </section>
    );
}
